// The owner's fresh second factor, asked where the server asks for it (QA-099, QA-685): handing out
// access (making or changing a role, a role's limits, taking back a permission, an invitation that
// gives the owner role) is refused with step_up_required until the owner gives a code. A call run through useStepUp() opens a
// code box then; a good code steps up and the call runs again. Cancelled, the refusal stands.
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { RpcError } from '@flowology/client';
import { stepUpWith } from './stepUp.ts';

/** The server asked for a fresh second factor first. */
export const askedStepUp = (e: unknown): boolean =>
  e instanceof RpcError && (e.detail as { reason?: string } | undefined)?.reason === 'step_up_required';

interface Asking {
  again: () => void;
  refuse: () => void;
}

/** `run(call)`: the call, asking for a code and running it again if the server wants one. Render `ask`. */
export function useStepUp(): { run: <T>(call: () => Promise<T>) => Promise<T>; ask: ReactNode } {
  const [asking, setAsking] = useState<Asking | null>(null);
  const run = useCallback(
    <T,>(call: () => Promise<T>): Promise<T> =>
      call().catch((e: unknown) => {
        if (!askedStepUp(e)) throw e;
        return new Promise<T>((resolve, reject) =>
          setAsking({ again: () => void call().then(resolve, reject), refuse: () => reject(e) }),
        );
      }),
    [],
  );
  const ask = asking && (
    <StepUpAsk
      onDone={() => {
        setAsking(null);
        asking.again();
      }}
      onCancel={() => {
        setAsking(null);
        asking.refuse();
      }}
    />
  );
  return { run, ask };
}

function StepUpAsk({ onDone, onCancel }: { onDone: () => void; onCancel: () => void }) {
  const ref = useRef<HTMLDialogElement>(null);
  const done = useRef(false);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
  }, []);
  return (
    <dialog
      ref={ref}
      className="sheet ask"
      aria-labelledby="step-up-title"
      onClose={() => {
        if (!done.current) onCancel();
      }}
    >
      <form
        method="dialog"
        onSubmit={(e) => {
          e.preventDefault();
          setBusy(true);
          setError(null);
          void stepUpWith(code).then((up) => {
            if (!up.ok) {
              setError(up.message);
              setBusy(false);
              return;
            }
            done.current = true;
            ref.current?.close();
            onDone();
          });
        }}
      >
        <h3 id="step-up-title" className="sheet-title">
          Confirm it’s you
        </h3>
        <label>
          A code from your authenticator app
          <input
            className="field"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
        </label>
        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}
        <div className="row end">
          <button type="button" className="btn soft" onClick={() => ref.current?.close()}>
            Cancel
          </button>
          <button type="submit" className="btn primary" disabled={busy || !code.trim()}>
            {busy ? 'Checking…' : 'Confirm'}
          </button>
        </div>
      </form>
    </dialog>
  );
}
